import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  featured?: boolean;
  onConsultationClick?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, featured = false, onConsultationClick }) => {
  return (
    <motion.div
      className={`group relative overflow-hidden rounded-xl ${
        featured ? 'lg:col-span-2 lg:row-span-2' : ''
      } ${
        product.id === 'pro-8k-projector' ? 'projector-glass-effect' : 'glass-effect'
      }`}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={`/products/${product.id}`} className="block h-full">
        <div className={`relative ${
          featured 
            ? 'h-[400px] sm:h-[450px] lg:h-[500px]' 
            : 'h-[350px] sm:h-[380px]'
        }`}>
          {/* Product Image */}
          <div className="absolute inset-0">
            <div
              className="w-full h-full bg-cover bg-center bg-no-repeat transition-transform duration-700 group-hover:scale-105"
              style={{
                backgroundImage: `url(${
                  product.id === 'pro-8k-projector' 
                    ? '/images/lens-image.png' 
                    : product.images.hero
                })`,
                backgroundColor: '#1a1a1a',
                transform: product.id === 'pro-8k-projector' ? 'scaleX(-1)' : 'none'
              }}
            />
            <div className={`absolute inset-0 ${
              product.id === 'pro-8k-projector'
                ? 'bg-gradient-to-t from-black/90 via-black/60 to-black/25'
                : 'bg-gradient-to-t from-black/80 via-black/20 to-transparent'
            }`} />

            {/* Enhanced optical overlay for projector */}
            {product.id === 'pro-8k-projector' && (
              <>
                {/* Additional left-side gradient for better text contrast */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
                
                {/* Subtle lens flare effect */}
                <div className="absolute inset-0 bg-gradient-radial from-highlight/5 via-transparent to-transparent opacity-60" />
                
                {/* Precision engineering accent */}
                <div className="absolute top-6 right-6">
                  <div className="w-2 h-2 bg-highlight rounded-full animate-pulse opacity-80" />
                </div>
              </>
            )}
          </div>

          {/* Content */}
          <div className="absolute inset-0 p-3 sm:p-6 lg:p-10 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Category Badge */}
              <div className="mb-3 sm:mb-4">
                <span className="inline-block px-2 py-1 sm:px-3 sm:py-1 bg-highlight/20 text-highlight text-xs font-semibold rounded-full">
                  {product.category}
                </span>
              </div>

              {/* Product Name */}
              <h3 className={`font-bold mb-2 sm:mb-3 text-accent group-hover:text-gradient transition-colors ${
                featured ? 'text-2xl sm:text-3xl lg:text-4xl' : 'text-lg sm:text-xl lg:text-2xl'
              }`}>
                {product.name}
              </h3>

              {/* Tagline */}
              <p className={`text-secondary mb-3 sm:mb-4 leading-relaxed ${
                featured ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
              }`}>
                {product.tagline}
              </p>

              {/* Price Range */}
              <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
                <span className={`font-semibold text-accent ${
                  featured ? 'text-lg sm:text-xl' : 'text-base sm:text-lg'
                }`}>
                  {product.priceRange}
                </span>
                {product.awards && product.awards.length > 0 && (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-highlight mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-xs text-highlight">Award Winner</span>
                  </div>
                )}
              </div>

              {/* Key Features */}
              <div className="mb-3 sm:mb-4 lg:mb-6">
                <ul className="space-y-1">
                  {product.features.slice(0, featured ? 4 : 3).map((feature, index) => (
                    <li key={index} className="text-xs sm:text-sm text-secondary flex items-center">
                      <div className="w-1 h-1 bg-highlight rounded-full mr-3" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="flex items-center justify-between">
                <motion.div
                  className="flex items-center text-highlight font-medium group-hover:text-accent transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <span className="mr-2">Discover More</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.div>
                
                {/* Consultation Button */}
                {onConsultationClick && (
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onConsultationClick();
                    }}
                    className="btn-secondary text-xs px-4 py-2 whitespace-nowrap"
                  >
                    Schedule Consultation
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Hover Effect Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-highlight/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard; 